import { useState, useEffect, useCallback} from 'react'
import { Form, Button, Row, Col, Alert, Tabs, Tab } from 'react-bootstrap'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api'
import { traerTodasLasPaginas } from '../../utils/paginacionApi'
import { esPersonaFisica } from '../../utils/tipoPersona'
import FielClienteSection from './FielClienteSection'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function normalizeEmailListInput(value) {
  return (value || '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean)
    .join(', ')
}

function validateEmailListInput(value) {
  const normalized = normalizeEmailListInput(value)
  const emails = normalized ? normalized.split(',').map((e) => e.trim()) : []
  if (!emails.length) return { valid: false, message: 'Email es requerido', normalized }
  const invalid = emails.find((e) => !EMAIL_RE.test(e))
  if (invalid) return { valid: false, message: `Email inválido en la lista: ${invalid}`, normalized }
  return { valid: true, message: '', normalized }
}

const ClientForm = ({ client, onSubmit, onCancel, isLoading, apiErrors }) => {
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    id: null, // ID del cliente (para actualizaciones)
    tipo_cliente: 'Persona Física',
    rfc: '',
    email: '',
    telefono: '',
    // Persona Física
    nombre: '',
    apellido_paterno: '',
    apellido_materno: '',
    fecha_nacimiento: '',
    curp: '',
    // Persona Moral
    razon_social: '',
    representante_legal: '',
    acta_constitutiva: '',
    fecha_constitucion: '',
    // Dirección fiscal
    calle: '',
    numero_exterior: '',
    numero_interior: '',
    colonia: '',
    municipio: '',
    estado_fiscal: '',
    estado: 'activo', // Estado del cliente: activo, inactivo, suspendido, baja
    // Solo para receptores de gobierno que exigen cuadrar la retención al centavo.
    permite_ajuste_manual_retenciones: false,
    codigo_postal: '',
    pais: 'México',
    nombre_comercial_pdf: '',
    // Configuración contable
    tipo_cierre: 'mensual',
    iva_default: 16,
    moneda: 'MXN',
    uso_cfdi: 'G03',
    activo: true,
    contador: '', // ID del contador asignado
    clave_prefijo: '',
    cuenta_contable_activo: '', // ID de cuenta tipo ACTIVO del plan de cuentas
    cuenta_principal_cobros: '', // ID de cuenta bancaria principal para cobros (solo en edición)
    clabe_cobros: '', // CLABE de la cuenta principal (SPEI en PDF de facturas)
  })

  const queryClient = useQueryClient()
  const [clientTypes, setClientTypes] = useState([])
  const [regimenesFiscales, setRegimenesFiscales] = useState([])
  const [contadores, setContadores] = useState([])
  const [cuentasActivo, setCuentasActivo] = useState([])
  const [errors, setErrors] = useState({})
  const [validatingRFC, setValidatingRFC] = useState(false)
  
  // Verificar si el usuario es administrador o superadmin
  const isAdmin = user && (user.is_superuser || user.role === 'admin' || user.role === 'superadmin')

  // Cuentas bancarias del cliente (solo al editar)
  const clienteId = client?.id
  const { data: bankAccounts = [], isFetched: bankAccountsFetched } = useQuery(
    ['bank-accounts-for-client', clienteId],
    async () => {
      if (!clienteId) return []
      const res = await api.get('/financial/bank-accounts/', {
        params: { cliente_id: clienteId, is_active: true }
      })
      return res.data.results || res.data || []
    },
    { enabled: !!clienteId }
  )

  // Sincronizar select + CLABE desde la cuenta marcada como principal (fuente de verdad)
  useEffect(() => {
    if (!client?.id || !bankAccountsFetched) return
    const principal = bankAccounts.find(acc => acc.es_cuenta_principal === true)
    const principalId = principal ? String(principal.id) : ''
    setFormData(prev => ({
      ...prev,
      cuenta_principal_cobros: principalId,
      clabe_cobros: principal ? (principal.clabe || '') : '',
    }))
  }, [client?.id, bankAccounts, bankAccountsFetched])

  // Al cambiar manualmente la cuenta principal en el select, cargar su CLABE
  const handleCuentaPrincipalChange = (e) => {
    const cuentaId = e.target.value
    const selected = bankAccounts.find(acc => String(acc.id) === String(cuentaId))
    setFormData(prev => ({
      ...prev,
      cuenta_principal_cobros: cuentaId,
      clabe_cobros: selected?.clabe || '',
    }))
  }

  // Sincronizar errores del API cuando cambien
  useEffect(() => {
    if (apiErrors) {
      setErrors(prev => ({ ...prev, ...apiErrors }))
    }
  }, [apiErrors])

  useEffect(() => {
    // Cargar tipos de cliente
    api.get('/clients/types/')
      .then(res => {
        setClientTypes(res.data.results || res.data || [])
      })
      .catch(err => {
        console.error('Error al cargar tipos de cliente:', err)
      })

    // Si hay cliente, cargar datos
    if (client) {
      console.log('Cargando cliente en formulario:', client)
      console.log('Tipo de cliente detectado:', {
        type_name: client.type_name,
        type_tax_regime: client.type_tax_regime,
        regimen_fiscal: client.regimen_fiscal,
        regimen_fiscal_id: client.regimen_fiscal?.id ?? client.regimen_fiscal
      })
      // Preparar datos del cliente asegurando que todos los campos estén presentes
      const tipoClienteDetectado = client.type_name || (client.type_tax_regime === 'fisica' ? 'Persona Física' : 'Persona Moral')
      console.log('Tipo de cliente a usar:', tipoClienteDetectado)
      setFormData(prev => ({
        ...prev,
        // Mapear solo campos del cliente (no pisar cuenta_principal_cobros / clabe_cobros
        // que se sincronizan desde bank-accounts)
        business_name: client.business_name ?? prev.business_name,
        rfc: client.rfc ?? prev.rfc,
        email: client.email ?? prev.email,
        phone: client.phone ?? prev.phone,
        telefono: client.telefono ?? prev.telefono,
        nombre: client.nombre ?? prev.nombre,
        apellido_paterno: client.apellido_paterno ?? prev.apellido_paterno,
        apellido_materno: client.apellido_materno ?? prev.apellido_materno,
        razon_social: client.razon_social ?? prev.razon_social,
        curp: client.curp ?? prev.curp,
        representante_legal: client.representante_legal ?? prev.representante_legal,
        acta_constitutiva: client.acta_constitutiva ?? prev.acta_constitutiva,
        type: client.type ?? prev.type,
        tipo_cliente: tipoClienteDetectado,
        calle: client.calle ?? '',
        numero_exterior: client.numero_exterior ?? '',
        numero_interior: client.numero_interior ?? '',
        colonia: client.colonia ?? '',
        municipio: client.municipio ?? '',
        estado_fiscal: client.estado_fiscal ?? '',
        codigo_postal: client.codigo_postal ?? '',
        pais: client.pais ?? 'México',
        nombre_comercial_pdf: client.nombre_comercial_pdf ?? '',
        fecha_nacimiento: client.fecha_nacimiento ?? '',
        fecha_constitucion: client.fecha_constitucion ?? '',
        estado: client.estado ?? (client.is_active ? 'activo' : 'inactivo'),
        permite_ajuste_manual_retenciones: client.permite_ajuste_manual_retenciones ?? false,
        activo: client.is_active !== undefined ? client.is_active : (client.estado === 'activo'),
        contador: client.contador ?? (client.contador_id ?? ''),
        tipo_cierre: client.tipo_cierre ?? 'mensual',
        iva_default: client.iva_default ?? 16,
        moneda: client.moneda ?? 'MXN',
        uso_cfdi: client.uso_cfdi ?? 'G03',
        regimen_fiscal: client.regimen_fiscal?.id ?? client.regimen_fiscal ?? '',
        clave_prefijo: client.clave_prefijo ?? '',
        cuenta_contable_activo: client.cuenta_contable_activo ?? (client.cuenta_contable_activo_id ?? ''),
      }))
      console.log('FormData después de cargar cliente (cobros se sincronizan aparte)')
      
      // Verificar específicamente estado_fiscal
      console.log('Verificación estado_fiscal:', {
        'client.estado_fiscal': client.estado_fiscal,
        'typeof': typeof client.estado_fiscal,
        'is null': client.estado_fiscal === null,
        'is undefined': client.estado_fiscal === undefined,
        'is empty string': client.estado_fiscal === ''
      })
    }
  }, [client])

  // Función para cargar regímenes fiscales
  const loadRegimenesFiscales = useCallback((tipoCliente) => {
    if (!tipoCliente) {
      return
    }
    
    // Por el `tipo` del catalogo ('fisica' / 'moral'), no por el nombre. Comparar el nombre
    // dejaba fuera a **'Persona Fisica con Actividad Empresarial'** --un tipo real en
    // produccion-- que caia en la rama de persona moral: se le ofrecian los 7 regimenes de PM
    // y ninguno de los 14 que le aplican. El regimen va en el CFDI.
    const fisica = esPersonaFisica(tipoCliente, clientTypes)
    
    // Construir parámetros de filtrado
    const params = fisica
      ? { aplica_fisica: 'true' }
      : { aplica_moral: 'true' }
    
    console.log(`Cargando regímenes fiscales para: ${tipoCliente}`, params)
    
    // Cargar regímenes fiscales filtrados
    api.get('/clients/regimenes-fiscales/', { params })
      .then(res => {
        const regimenes = res.data.results || res.data || []
        setRegimenesFiscales(regimenes)
        console.log(`Regímenes fiscales cargados para ${tipoCliente}:`, regimenes.length, regimenes)
        if (regimenes.length === 0) {
          console.warn(`No se encontraron regímenes fiscales para ${tipoCliente}`)
        }
      })
      .catch(err => {
        console.error('Error al cargar regímenes fiscales:', err)
        toast.error('Error al cargar regímenes fiscales.')
        setRegimenesFiscales([])
      })
  }, [clientTypes])

  // Cargar regímenes fiscales cuando se carga un cliente para editar (prioridad)
  useEffect(() => {
    if (client) {
      const tipoClienteDetectado = client.type_name || (client.type_tax_regime === 'fisica' ? 'Persona Física' : 'Persona Moral')
      console.log('Cargando regímenes fiscales al cargar cliente para editar:', tipoClienteDetectado, {
        type_name: client.type_name,
        type_tax_regime: client.type_tax_regime
      })
      loadRegimenesFiscales(tipoClienteDetectado)
    }
  }, [client, loadRegimenesFiscales])

  // Cargar regímenes fiscales cuando cambia el tipo de cliente en el formulario (solo si no hay cliente cargado)
  useEffect(() => {
    // Solo cargar si no hay cliente cargado (para evitar conflictos al editar)
    if (!client && formData.tipo_cliente) {
      console.log('Cargando regímenes fiscales por cambio de tipo_cliente:', formData.tipo_cliente)
      loadRegimenesFiscales(formData.tipo_cliente)
    }
  }, [formData.tipo_cliente, client, loadRegimenesFiscales])

  // Sincronizar régimen fiscal inicial solo cuando cargan los regímenes (no revertir cambios del usuario)
  useEffect(() => {
    if (!client || regimenesFiscales.length === 0) return
    const regimenId = client.regimen_fiscal?.id ?? client.regimen_fiscal
    if (!regimenId) return
    const regimenExists = regimenesFiscales.some(r => r.id === regimenId)
    if (!regimenExists) {
      console.warn(`Régimen fiscal ${regimenId} no está en la lista para ${formData.tipo_cliente}`)
      return
    }
    // Solo establecer si formData.regimen_fiscal está vacío (carga inicial), nunca sobrescribir cambios del usuario
    setFormData(prev => {
      if (prev.regimen_fiscal) return prev
      return { ...prev, regimen_fiscal: regimenId }
    })
  }, [regimenesFiscales, client, formData.tipo_cliente])

  // Cargar lista de contadores si el usuario es administrador
  useEffect(() => {
    if (isAdmin) {
      api.get('/auth/users/', { params: { role: 'contador', activo: 'true' } })
        .then(res => {
          const contadoresList = res.data.results || res.data || []
          setContadores(contadoresList.filter(c => c.activo !== false && c.is_active !== false))
        })
        .catch(err => {
          console.error('Error al cargar contadores:', err)
          setContadores([])
        })
    }
  }, [isAdmin])

  // Cargar cuentas contables tipo ACTIVO (para selector cuenta contable de activo)
  // Al editar cliente existente, incluir cliente_id para mostrar cuentas personalizadas
  useEffect(() => {
    const params = { account_type: 'ACTIVO', is_active: 'true' }
    if (client?.id) {
      params.cliente_id = client.id
    }
    // Todas las paginas, no solo la primera: el catalogo de ACTIVO tiene 343 cuentas y el
    // endpoint pagina de 50 en 50, asi que leyendo solo `results` quedaban invisibles mas de
    // la mitad de las cuentas de clientes (ver utils/paginacionApi.js).
    let cancelado = false
    traerTodasLasPaginas(api, '/accounting/accounts/', params, {
      onError: (err) => console.error('Error al cargar cuentas activo:', err),
    }).then(cuentas => {
      if (!cancelado) setCuentasActivo(cuentas)
    })
    return () => { cancelado = true }
  }, [client?.id])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    const newValue = type === 'checkbox' ? checked : value
    
    // Si cambia el tipo de cliente, limpiar el régimen fiscal
    // porque los regímenes fiscales son diferentes para personas físicas y morales
    if (name === 'tipo_cliente') {
      setFormData(prev => ({
        ...prev,
        [name]: newValue,
        regimen_fiscal: '' // Limpiar régimen fiscal al cambiar tipo de cliente
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: newValue
      }))
    }
    
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const validateRFC = async (rfc) => {
    if (!rfc) return true
    
    setValidatingRFC(true)
    try {
      // Validación básica de formato RFC
      const rfcPattern = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/
      if (!rfcPattern.test(rfc)) {
        setErrors(prev => ({ ...prev, rfc: 'Formato de RFC inválido' }))
        return false
      }
      
      // Aquí se podría agregar validación con API externa en el futuro
      return true
    } catch (error) {
      return true
    } finally {
      setValidatingRFC(false)
    }
  }

  const handleRFCBlur = async (e) => {
    await validateRFC(e.target.value)
  }

  const validateCURP = (curp) => {
    if (!curp) return true // CURP es opcional
    
    const curpUpper = curp.toUpperCase().trim()
    
    // Validar longitud
    if (curpUpper.length !== 18) {
      setErrors(prev => ({ ...prev, curp: 'CURP inválido. Debe tener exactamente 18 caracteres.' }))
      return false
    }
    
    // Validar formato: 4 letras + 6 dígitos + 1 letra (H/M) + 5 caracteres alfanuméricos + 1 carácter alfanumérico + 1 dígito
    const curpPattern = /^[A-Z]{4}\d{6}[HM][A-Z0-9]{5}[0-9A-Z]\d$/
    if (!curpPattern.test(curpUpper)) {
      setErrors(prev => ({ ...prev, curp: 'CURP inválido. Debe tener el formato correcto (18 caracteres alfanuméricos).' }))
      return false
    }
    
    // Limpiar error si es válido
    setErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors.curp
      return newErrors
    })
    return true
  }

  const handleCURPBlur = (e) => {
    validateCURP(e.target.value)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    console.log('handleSubmit llamado - formData:', formData)
    console.log('client:', client)
    
    // Validar que los tipos de cliente estén cargados
    if (clientTypes.length === 0) {
      console.error('Error: clientTypes no cargados')
      setErrors({ tipo_cliente: 'Cargando tipos de cliente. Por favor, espera un momento e intenta de nuevo.' })
      return
    }
    
    // Validaciones básicas
    const newErrors = {}
    
    if (!formData.rfc) newErrors.rfc = 'RFC es requerido'
    const emailValidation = validateEmailListInput(formData.email)
    if (!emailValidation.valid) newErrors.email = emailValidation.message
    if (!formData.calle) newErrors.calle = 'Calle es requerida'
    if (!formData.numero_exterior) newErrors.numero_exterior = 'Número exterior es requerido'
    if (!formData.colonia) newErrors.colonia = 'Colonia es requerida'
    if (!formData.municipio) newErrors.municipio = 'Alcaldía es requerida'
    if (!formData.estado_fiscal) newErrors.estado_fiscal = 'Estado es requerido'
    if (!formData.codigo_postal) newErrors.codigo_postal = 'Código postal es requerido'
    
    if (formData.tipo_cliente === 'Persona Física') {
      if (!formData.nombre) newErrors.nombre = 'Nombre es requerido'
      if (!formData.apellido_paterno) newErrors.apellido_paterno = 'Apellido paterno es requerido'
      // Validar CURP si está presente (es opcional pero debe ser válido si se proporciona)
      if (formData.curp && !validateCURP(formData.curp)) {
        // El error ya se estableció en validateCURP
        if (!newErrors.curp) {
          newErrors.curp = 'CURP inválido'
        }
      }
    } else {
      if (!formData.razon_social) newErrors.razon_social = 'Razón social es requerida'
    }
    
    // Validar contador si el usuario es administrador
    if (isAdmin && !formData.contador) {
      newErrors.contador = 'Debe seleccionar un contador para asignar al cliente'
    }
    
    console.log('Validación - formData.estado_fiscal:', formData.estado_fiscal)
    console.log('Validación - formData.estado:', formData.estado)
    console.log('Validación - newErrors:', newErrors)
    
    if (Object.keys(newErrors).length > 0) {
      console.error('Errores de validación detectados:', newErrors)
      setErrors(newErrors)
      // Mostrar el primer error en un toast para que el usuario lo vea
      const firstError = Object.values(newErrors)[0]
      if (firstError) {
        toast.error(firstError)
      }
      return
    }
    
    console.log('Validación pasada, preparando datos para enviar...')
    
    // Buscar el ClientType correspondiente
    const tipoClienteString = formData.tipo_cliente
    const tipoClienteMap = {
      'Persona Física': 'fisica',
      'Persona Moral': 'moral'
    }
    const tipoClienteValue = tipoClienteMap[tipoClienteString]
    
    // Buscar el ClientType en la lista cargada (tipo, codigo o descripcion como fallback)
    let clientType = clientTypes.find(ct => ct.tipo === tipoClienteValue)
    if (!clientType && tipoClienteValue === 'fisica') {
      // Fallback: tipo vacío en BD (migración 0009 lo corrige). CT001/CT003 = Persona Física
      clientType = clientTypes.find(ct => ct.codigo === 'CT001') || clientTypes.find(ct =>
        ct.descripcion?.includes('Personas físicas') && !ct.descripcion?.includes('RIF')
      )
    }
    if (!clientType && tipoClienteValue === 'moral') {
      clientType = clientTypes.find(ct => ct.codigo === 'CT002') || clientTypes.find(ct =>
        ct.descripcion?.includes('personas morales')
      )
    }
    if (!clientType) {
      console.error('ClientTypes disponibles:', clientTypes)
      console.error('Buscando tipo:', tipoClienteValue)
      setErrors({ tipo_cliente: `Tipo de cliente "${tipoClienteString}" no encontrado. Tipos disponibles: ${clientTypes.map(ct => ct.nombre || ct.codigo || ct.descripcion).filter(Boolean).join(', ')}` })
      return
    }
    
    // Preparar datos para enviar
    const submitData = { ...formData }
    
    // Mapear tipo_cliente a type (ID del ClientType)
    submitData.type = clientType.id
    delete submitData.tipo_cliente
    submitData.email = normalizeEmailListInput(submitData.email)
    
    // Limpiar campos según tipo de cliente
    if (formData.tipo_cliente === 'Persona Física') {
      delete submitData.razon_social
      delete submitData.representante_legal
      delete submitData.acta_constitutiva
      delete submitData.fecha_constitucion
    } else {
      delete submitData.nombre
      delete submitData.apellido_paterno
      delete submitData.apellido_materno
      delete submitData.fecha_nacimiento
      delete submitData.curp
    }
    
    // Mapear campos del formulario a los campos del modelo
    if (submitData.regimen_fiscal) {
      // regimen_fiscal ya es un ID, está bien
    }
    
    // Mapear estado a is_active (legacy) para compatibilidad
    // El estado ya viene del select, solo mapeamos is_active para compatibilidad
    submitData.is_active = (submitData.estado === 'activo')
    
    // Si se seleccionó un contador, enviarlo (solo para administradores)
    if (submitData.contador) {
      // El contador ya es un ID, está bien
    } else {
      // Si no se seleccionó contador, eliminar el campo para que el backend lo asigne automáticamente
      delete submitData.contador
    }
    
    // Asegurar que el ID se incluya si estamos editando
    if (client && client.id) {
      submitData.id = client.id
    }
    
    // Asegurar que estado_fiscal siempre se envíe, incluso si está vacío
    if (submitData.estado_fiscal === undefined || submitData.estado_fiscal === null) {
      submitData.estado_fiscal = ''
    }
    // Cuenta contable de activo: enviar null si está vacío
    if (!submitData.cuenta_contable_activo) {
      submitData.cuenta_contable_activo = null
    }

    // Actualizar cuenta principal para cobros y CLABE (solo al editar, al guardar)
    const cuentaPrincipalId = submitData.cuenta_principal_cobros ? String(submitData.cuenta_principal_cobros) : ''
    const clabeCobros = String(submitData.clabe_cobros || '').replace(/\D/g, '')
    delete submitData.cuenta_principal_cobros
    delete submitData.clabe_cobros
    if (client?.id && cuentaPrincipalId) {
      const principalActual = bankAccounts.find(acc => acc.es_cuenta_principal === true)
      const cuentaSel = bankAccounts.find(acc => String(acc.id) === cuentaPrincipalId)
      const patch = {}
      if (!principalActual || String(principalActual.id) !== cuentaPrincipalId) {
        patch.es_cuenta_principal = true
      }
      const clabeActual = (cuentaSel?.clabe || '').replace(/\D/g, '')
      if (clabeCobros !== clabeActual) {
        if (clabeCobros && clabeCobros.length !== 18) {
          toast.error('La CLABE debe tener exactamente 18 dígitos')
          setErrors(prev => ({ ...prev, clabe_cobros: 'La CLABE debe tener exactamente 18 dígitos' }))
          return
        }
        patch.clabe = clabeCobros
      }
      if (Object.keys(patch).length > 0) {
        try {
          await api.patch(`/financial/bank-accounts/${cuentaPrincipalId}/`, patch)
          queryClient.invalidateQueries('bank-accounts')
          queryClient.invalidateQueries(['bank-accounts-for-client', client.id])
          queryClient.invalidateQueries('clients')
        } catch (err) {
          const msg = err.response?.data?.clabe?.[0] || 'Error al actualizar la cuenta de cobros / CLABE'
          toast.error(msg)
          return
        }
      }
    }
    
    // Verificar que el estado esté incluido
    console.log('Enviando datos del cliente:', submitData)
    console.log('Estado en submitData:', submitData.estado)
    console.log('Estado en formData:', formData.estado)
    console.log('is_active en submitData:', submitData.is_active)
    console.log('estado_fiscal en submitData:', submitData.estado_fiscal)
    console.log('estado_fiscal en formData:', formData.estado_fiscal)
    console.log('estado_fiscal type:', typeof submitData.estado_fiscal)
    console.log('estado_fiscal length:', submitData.estado_fiscal?.length)
    
    onSubmit(submitData)
  }

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <Tabs defaultActiveKey="general" className="mb-3">
        <Tab eventKey="general" title="Datos Generales">
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Prefijo de clave (Cartera)</Form.Label>
                <Form.Control
                  type="text"
                  name="clave_prefijo"
                  value={formData.clave_prefijo || ''}
                  onChange={handleChange}
                  placeholder="Ej. 020"
                />
                <Form.Text className="text-muted">
                  {formData.tipo_cliente === 'Persona Moral'
                    ? 'Se muestra en Cartera como: Prefijo - Razón Social'
                    : 'Se muestra en Cartera como: Prefijo - Nombre Apellido'}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Tipo de Cliente *</Form.Label>
                <Form.Select
                  name="tipo_cliente"
                  value={formData.tipo_cliente}
                  onChange={handleChange}
                  required
                >
                  <option value="Persona Física">Persona Física</option>
                  <option value="Persona Moral">Persona Moral</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>RFC *</Form.Label>
                <Form.Control
                  type="text"
                  name="rfc"
                  value={formData.rfc}
                  onChange={handleChange}
                  onBlur={handleRFCBlur}
                  isInvalid={!!errors.rfc}
                  required
                  disabled={validatingRFC}
                />
                {errors.rfc && <Form.Control.Feedback type="invalid">{errors.rfc}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
          </Row>

          {formData.tipo_cliente === 'Persona Física' ? (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Nombre *</Form.Label>
                    <Form.Control
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      isInvalid={!!errors.nombre}
                      required
                    />
                    {errors.nombre && <Form.Control.Feedback type="invalid">{errors.nombre}</Form.Control.Feedback>}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Apellido Paterno *</Form.Label>
                    <Form.Control
                      type="text"
                      name="apellido_paterno"
                      value={formData.apellido_paterno}
                      onChange={handleChange}
                      isInvalid={!!errors.apellido_paterno}
                      required
                    />
                    {errors.apellido_paterno && <Form.Control.Feedback type="invalid">{errors.apellido_paterno}</Form.Control.Feedback>}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Apellido Materno</Form.Label>
                    <Form.Control
                      type="text"
                      name="apellido_materno"
                      value={formData.apellido_materno}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Fecha de Nacimiento</Form.Label>
                    <Form.Control
                      type="date"
                      name="fecha_nacimiento"
                      value={formData.fecha_nacimiento}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>CURP</Form.Label>
                    <Form.Control
                      type="text"
                      name="curp"
                      value={formData.curp}
                      onChange={handleChange}
                      onBlur={handleCURPBlur}
                      isInvalid={!!errors.curp}
                      maxLength={18}
                      style={{ textTransform: 'uppercase' }}
                    />
                    {errors.curp && (
                      <Form.Control.Feedback type="invalid">
                        {errors.curp}
                      </Form.Control.Feedback>
                    )}
                    <Form.Text className="text-muted">
                      CURP debe tener exactamente 18 caracteres alfanuméricos
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </>
          ) : (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Razón Social *</Form.Label>
                    <Form.Control
                      type="text"
                      name="razon_social"
                      value={formData.razon_social}
                      onChange={handleChange}
                      isInvalid={!!errors.razon_social}
                      required
                    />
                    {errors.razon_social && <Form.Control.Feedback type="invalid">{errors.razon_social}</Form.Control.Feedback>}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Representante Legal</Form.Label>
                    <Form.Control
                      type="text"
                      name="representante_legal"
                      value={formData.representante_legal}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Acta Constitutiva</Form.Label>
                    <Form.Control
                      type="text"
                      name="acta_constitutiva"
                      value={formData.acta_constitutiva}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Fecha de Constitución</Form.Label>
                    <Form.Control
                      type="date"
                      name="fecha_constitucion"
                      value={formData.fecha_constitucion}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Email(s) *</Form.Label>
                <Form.Control
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                  required
                  placeholder="correo1@dominio.com, correo2@dominio.com"
                />
                {errors.email && <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>}
                <Form.Text className="text-muted">
                  Puede capturar uno o varios correos separados por coma.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Teléfono</Form.Label>
                <Form.Control
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="fiscal" title="Datos Fiscales">
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Calle *</Form.Label>
                <Form.Control
                  type="text"
                  name="calle"
                  value={formData.calle}
                  onChange={handleChange}
                  isInvalid={!!errors.calle}
                  required
                />
                {errors.calle && <Form.Control.Feedback type="invalid">{errors.calle}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Número Exterior *</Form.Label>
                <Form.Control
                  type="text"
                  name="numero_exterior"
                  value={formData.numero_exterior}
                  onChange={handleChange}
                  isInvalid={!!errors.numero_exterior}
                  required
                />
                {errors.numero_exterior && <Form.Control.Feedback type="invalid">{errors.numero_exterior}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Número Interior</Form.Label>
                <Form.Control
                  type="text"
                  name="numero_interior"
                  value={formData.numero_interior}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Colonia *</Form.Label>
                <Form.Control
                  type="text"
                  name="colonia"
                  value={formData.colonia}
                  onChange={handleChange}
                  isInvalid={!!errors.colonia}
                  required
                />
                {errors.colonia && <Form.Control.Feedback type="invalid">{errors.colonia}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Alcaldía *</Form.Label>
                <Form.Control
                  type="text"
                  name="municipio"
                  value={formData.municipio}
                  onChange={handleChange}
                  isInvalid={!!errors.municipio}
                  required
                  placeholder="Alcaldía"
                />
                {errors.municipio && <Form.Control.Feedback type="invalid">{errors.municipio}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Estado *</Form.Label>
                <Form.Control
                  type="text"
                  name="estado_fiscal"
                  value={formData.estado_fiscal}
                  onChange={handleChange}
                  isInvalid={!!errors.estado_fiscal}
                  required
                />
                {errors.estado_fiscal && <Form.Control.Feedback type="invalid">{errors.estado_fiscal}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Código Postal *</Form.Label>
                <Form.Control
                  type="text"
                  name="codigo_postal"
                  value={formData.codigo_postal}
                  onChange={handleChange}
                  isInvalid={!!errors.codigo_postal}
                  required
                />
                {errors.codigo_postal && <Form.Control.Feedback type="invalid">{errors.codigo_postal}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>País</Form.Label>
                <Form.Control
                  type="text"
                  name="pais"
                  value={formData.pais}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Régimen Fiscal</Form.Label>
                <Form.Select
                  name="regimen_fiscal"
                  value={formData.regimen_fiscal || ''}
                  onChange={handleChange}
                >
                  <option value="">Seleccionar...</option>
                  {regimenesFiscales.map(regimen => (
                    <option key={regimen.id} value={regimen.id}>
                      {regimen.codigo} - {regimen.descripcion}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Nombre Comercial (PDF)</Form.Label>
                <Form.Control
                  type="text"
                  name="nombre_comercial_pdf"
                  value={formData.nombre_comercial_pdf || ''}
                  onChange={handleChange}
                  placeholder="Nombre a mostrar en encabezado del PDF"
                />
                <Form.Text className="text-muted">
                  Campo opcional. Se usa como valor prioritario en facturas.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <hr className="my-4" />
          <h5 className="mb-3">e.firma (FIEL) — Descarga Masiva SAT</h5>
          {!client?.id ? (
            <Alert variant="info">
              Guarde el cliente primero para configurar la FIEL (certificado .cer, llave .key y contraseña).
            </Alert>
          ) : (
            <FielClienteSection clientId={client.id} clientRfc={formData.rfc || client.rfc} />
          )}
        </Tab>

        <Tab eventKey="contable" title="Configuración Contable">
          {client && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Cuenta principal para cobros</Form.Label>
                  {bankAccounts.length === 0 ? (
                    <Alert variant="info" className="mb-0">
                      Sin cuenta bancaria, favor de registrar una cuenta en Financiero → Cuentas Bancarias
                    </Alert>
                  ) : (
                    <Form.Select
                      name="cuenta_principal_cobros"
                      value={formData.cuenta_principal_cobros || ''}
                      onChange={handleCuentaPrincipalChange}
                    >
                      <option value="">Seleccionar...</option>
                      {bankAccounts.map(acc => (
                        <option key={acc.id} value={String(acc.id)}>
                          {acc.name || ''} {acc.account_number || ''} - {acc.bank_name || ''}
                          {acc.clabe ? ` (CLABE ${acc.clabe})` : ''}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>CLABE (SPEI)</Form.Label>
                  <Form.Control
                    type="text"
                    name="clabe_cobros"
                    value={formData.clabe_cobros || ''}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 18)
                      setFormData(prev => ({ ...prev, clabe_cobros: digits }))
                    }}
                    placeholder="18 dígitos"
                    maxLength={18}
                    disabled={!formData.cuenta_principal_cobros}
                    isInvalid={!!errors.clabe_cobros}
                  />
                  <Form.Control.Feedback type="invalid">{errors.clabe_cobros}</Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Se imprime en el PDF de facturas: a qué cuenta depositar. Vacío = no se muestra la leyenda.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Cuenta contable de activo</Form.Label>
                <Form.Select
                  name="cuenta_contable_activo"
                  value={formData.cuenta_contable_activo || ''}
                  onChange={handleChange}
                >
                  <option value="">Seleccionar...</option>
                  {cuentasActivo.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.code} - {c.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">Cuenta del plan de cuentas (tipo Activo) para este cliente</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Tipo de Cierre</Form.Label>
                <Form.Select
                  name="tipo_cierre"
                  value={formData.tipo_cierre}
                  onChange={handleChange}
                >
                  <option value="mensual">Mensual</option>
                  <option value="trimestral">Trimestral</option>
                  <option value="anual">Anual</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>IVA Default (%)</Form.Label>
                <Form.Control
                  type="number"
                  name="iva_default"
                  value={formData.iva_default}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="0.01"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Moneda</Form.Label>
                <Form.Select
                  name="moneda"
                  value={formData.moneda}
                  onChange={handleChange}
                >
                  <option value="MXN">MXN - Peso Mexicano</option>
                  <option value="USD">USD - Dólar Americano</option>
                  <option value="EUR">EUR - Euro</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Uso CFDI</Form.Label>
                <Form.Select
                  name="uso_cfdi"
                  value={formData.uso_cfdi}
                  onChange={handleChange}
                >
                  <option value="G01">G01 - Adquisición de mercancías</option>
                  <option value="G02">G02 - Devoluciones, descuentos o bonificaciones</option>
                  <option value="G03">G03 - Gastos en general</option>
                  <option value="I01">I01 - Construcciones</option>
                  <option value="I02">I02 - Mobiliario y equipo de oficina por inversiones</option>
                  <option value="I03">I03 - Equipo de transporte</option>
                  <option value="I04">I04 - Equipo de computo y accesorios</option>
                  <option value="I05">I05 - Dados, troqueles, moldes, matrices y herramental</option>
                  <option value="I06">I06 - Comunicaciones telefónicas</option>
                  <option value="I07">I07 - Comunicaciones satelitales</option>
                  <option value="I08">I08 - Otra maquinaria y equipo</option>
                  <option value="D01">D01 - Honorarios médicos, dentales y gastos hospitalarios</option>
                  <option value="D02">D02 - Gastos médicos por incapacidad o discapacidad</option>
                  <option value="D03">D03 - Gastos funerales</option>
                  <option value="D04">D04 - Donativos</option>
                  <option value="D05">D05 - Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)</option>
                  <option value="D06">D06 - Aportaciones voluntarias al SAR</option>
                  <option value="D07">D07 - Primas por seguros de gastos médicos</option>
                  <option value="D08">D08 - Gastos de transportación escolar obligatoria</option>
                  <option value="D09">D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones</option>
                  <option value="D10">D10 - Pagos por servicios educativos (colegiaturas)</option>
                  <option value="S01">S01 - Sin efectos fiscales</option>
                  <option value="CP01">CP01 - Pagos</option>
                  <option value="CN01">CN01 - Nómina</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Estado del Cliente *</Form.Label>
                <Form.Select
                  name="estado"
                  value={formData.estado}
                  onChange={handleChange}
                  isInvalid={!!errors.estado}
                  required
                >
                  <option value="">Seleccione un estado</option>
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                  <option value="suspendido">Suspendido</option>
                  <option value="baja">Dado de baja</option>
                </Form.Select>
                {errors.estado && <Form.Control.Feedback type="invalid">{errors.estado}</Form.Control.Feedback>}
                <Form.Text className="text-muted">
                  Estado del cliente en el sistema
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  id="permite-ajuste-retenciones"
                  name="permite_ajuste_manual_retenciones"
                  checked={Boolean(formData.permite_ajuste_manual_retenciones)}
                  onChange={handleChange}
                  label="Permitir ajustar a mano el importe de las retenciones"
                />
                <Form.Text className="text-muted">
                  Actívelo solo si este cliente, como receptor, exige cuadrar la retención al
                  centavo con su propio recibo (SEDENA, SEGOB y similares). El ajuste se mueve
                  únicamente dentro del rango que el SAT admite: no cambia la tasa ni el total.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          {isAdmin && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Contador Asignado *</Form.Label>
                  <Form.Select
                    name="contador"
                    value={formData.contador || ''}
                    onChange={handleChange}
                    isInvalid={!!errors.contador}
                    required
                  >
                    <option value="">Seleccione un contador</option>
                    {contadores.map(contador => (
                      <option key={contador.id} value={contador.id}>
                        {contador.username} {contador.first_name || contador.last_name ? `(${contador.first_name || ''} ${contador.last_name || ''})`.trim() : ''}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.contador && (
                    <Form.Control.Feedback type="invalid">
                      {errors.contador}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    Seleccione el contador responsable de este cliente
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
        </Tab>
      </Tabs>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          type="submit" 
          disabled={isLoading}
          onClick={() => console.log('Botón clickeado - isLoading:', isLoading, 'client:', client)}
        >
          {isLoading ? 'Guardando...' : (client ? 'Actualizar Cliente' : 'Crear Cliente')}
        </Button>
      </div>
    </Form>
  )
}

export default ClientForm

